import { useEffect, useState } from 'react';
import { useRoomStore } from '../../../stores/roomStore';
import type { RoomLogEntry } from '../../../types/room';

interface RoomActivityLogSectionProps {
  roomId: string;
}

/**
 * Section showing room activity log.
 */
export function RoomActivityLogSection({ roomId }: RoomActivityLogSectionProps) {
  const { getActivityLog } = useRoomStore();
  const [logs, setLogs] = useState<RoomLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLogs = async () => {
      setIsLoading(true);
      const entries = await getActivityLog(roomId);
      setLogs(entries);
      setIsLoading(false);
    };
    void loadLogs();
  }, [roomId, getActivityLog]);

  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Activity Log</h2>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-accent border-t-transparent rounded-full" />
        </div>
      ) : logs.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4">No activity yet</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex items-center justify-between py-2 text-sm"
            >
              <span>
                <span className="font-medium">{log.userName}</span>
                <span className="text-gray-400">
                  {' '}{log.action === 'entered' ? 'entered' : 'left'} the room
                </span>
              </span>
              <span className="text-gray-500 text-xs">
                {formatTimestamp(log.timestamp)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Formats a timestamp for display.
 */
function formatTimestamp(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}
